'use client'

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Send } from 'lucide-react'

// import { Check } from 'lucide-react'

import type { BookCollectionDetails, BookDetails } from '@package/api'
import type { RenderLink } from '@ui/web/utils'
import { Button, Skeleton, toast } from '@ui/web/components'
import { FaveIcon } from '@ui/web/icon'
import cn from '@config/tailwind/cn'

import type { CardAction } from '../components'
import { useSharedTRPC } from '../context'
import { ShareContentDialog } from '../lib/share-content-dialog'
import { BookCollectionDetailView } from './book-collection-detail-view'

interface FeaturedBookCollectionPageProps {
  slug: string
  backLabel?: string
  renderBackLink: RenderLink
  renderLoveBookCollectionLink: RenderLink
  renderBookAction: (book: BookDetails) => CardAction
  onTagClick?: (type: 'author' | 'publisher' | 'level', id: string) => void
  showSaveButton?: boolean
  /** Shows the "Bagikan" CTA. Callers gate this on the viewer being a main account. */
  withShareCta?: boolean
  /** Link out to "add a child" for the share dialog's empty state. */
  renderAddChildLink?: RenderLink
  className?: string
}

export function FeaturedBookCollectionPage({
  slug,
  backLabel = 'Kompilasi Buku',
  renderBackLink,
  renderLoveBookCollectionLink,
  renderBookAction,
  onTagClick,
  showSaveButton = true,
  withShareCta = false,
  renderAddChildLink,
  className,
}: FeaturedBookCollectionPageProps) {
  const trpc = useSharedTRPC()
  const [openShareDialog, setOpenShareDialog] = useState(false)
  const { data, isLoading } = useQuery(trpc.library.getBookCollectionDetails.queryOptions({ id: slug }))

  if (isLoading) {
    return (
      <main className={cn('srd:w-full srd:h-full srd:min-h-dvh', className)}>
        <div className='srd:w-full srd:max-w-[1200px] srd:mx-auto srd:flex srd:flex-col srd:gap-y-10 srd:px-4 srd:py-10'>
          <Skeleton className='srd:h-5 srd:w-32' />
          <div className='srd:grid srd:md:grid-cols-[auto_1fr] srd:grid-cols-1 srd:gap-x-6 srd:gap-y-6'>
            <Skeleton className='srd:aspect-5/4 srd:w-[280px] srd:rounded-lg' />
            <div className='srd:flex srd:flex-col srd:gap-y-4'>
              <Skeleton className='srd:h-8 srd:w-3/4' />
              <Skeleton className='srd:h-5 srd:w-20' />
              <Skeleton className='srd:h-16 srd:w-full' />
            </div>
          </div>
          <Skeleton className='srd:h-[208px] srd:w-full srd:rounded-lg' />
          <div className='srd:flex srd:flex-wrap srd:gap-2'>
            <Skeleton className='srd:h-7 srd:w-24 srd:rounded-full' />
            <Skeleton className='srd:h-7 srd:w-20 srd:rounded-full' />
            <Skeleton className='srd:h-7 srd:w-28 srd:rounded-full' />
            <Skeleton className='srd:h-7 srd:w-16 srd:rounded-full' />
          </div>
        </div>
      </main>
    )
  }

  if (!data) return null

  const activeBooks = data.bookCollectionToBooks.filter((v) => v.book.isActive)

  return (
    <>
      <BookCollectionDetailView
        className={className}
        books={activeBooks}
        name={data.name}
        description={data.description}
        backLabel={backLabel}
        renderBackLink={renderBackLink}
        renderBookAction={renderBookAction}
        onTagClick={onTagClick}
        cover={
          <img
            src={data.coverUrl}
            alt={data.name}
            width={280}
            height={224}
            draggable={false}
            className='srd:aspect-5/4 srd:w-full srd:rounded-lg srd:object-cover'
          />
        }
        cta={
          showSaveButton || withShareCta ? (
            <div className='srd:flex srd:flex-wrap srd:items-center srd:gap-x-2 srd:gap-y-2'>
              {showSaveButton && <SaveButton data={data} renderLoveBookCollectionLink={renderLoveBookCollectionLink} />}

              {withShareCta && (
                <Button
                  variant='ghost'
                  onClick={() => setOpenShareDialog(true)}
                  className='srd:w-fit srd:gap-x-2 srd:px-2 srd:text-neutral-white srd:hover:bg-transparent srd:hover:opacity-70'
                >
                  <Send className='srd:size-4' />
                  Bagikan
                </Button>
              )}
            </div>
          ) : undefined
        }
      />

      {withShareCta && (
        // `data.id` rather than the `slug` prop: the route accepts either, but the share
        // row and its notifications should key on the stable uuid, not a renameable slug.
        <ShareContentDialog
          open={openShareDialog}
          onOpenChange={setOpenShareDialog}
          entityType='BOOK_COLLECTION'
          entityId={data.id}
          cover={{ kind: 'bookCollection', coverUrl: data.coverUrl }}
          renderAddChildLink={renderAddChildLink}
        />
      )}
    </>
  )
}

interface SaveButtonProps {
  data: BookCollectionDetails
  renderLoveBookCollectionLink: RenderLink
}

function SaveButton({
  data,
  // renderLoveBookCollectionLink
}: SaveButtonProps) {
  const trpc = useSharedTRPC()
  const queryClient = useQueryClient()

  const doILoveThisQuery = useQuery(trpc.loveBook.doILoveThisBookCollection.queryOptions({ bookCollectionId: data.id }))

  const likeMutation = useMutation(
    trpc.loveBook.likeBookCollection.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.loveBook.doILoveThisBookCollection.queryKey({ bookCollectionId: data.id }),
        })
        await queryClient.invalidateQueries({
          queryKey: trpc.loveBook.getAllLovedBookCollections.infiniteQueryKey(),
        })
      },
    }),
  )

  const unlikeMutation = useMutation(
    trpc.loveBook.unlikeBookCollection.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.loveBook.doILoveThisBookCollection.queryKey({ bookCollectionId: data.id }),
        })
        await queryClient.invalidateQueries({
          queryKey: trpc.loveBook.getAllLovedBookCollections.infiniteQueryKey(),
        })
      },
    }),
  )

  const isLoved = doILoveThisQuery.data === true
  const isPending = likeMutation.isPending || unlikeMutation.isPending

  const handleClick = () => {
    // let toastId: string | number
    // let likeType: 'create' | 'delete'

    if (isLoved) {
      // toastId = toast.loading('Menghapus koleksi bacaan dari Pilihanku...')
      // likeType = 'delete'
      unlikeMutation.mutate(
        { bookCollectionId: data.id },
        {
          onSuccess: () => {
            // toast.dismiss(toastId)
            // toast.success('Koleksi bacaan berhasil dihapus dari Pilihanku')
          },
          onError: () => {
            // toast.dismiss(toastId)
            toast.error('Gagal menghapus koleksi bacaan dari Pilihanku')
          },
        },
      )
    } else {
      // toastId = toast.loading('Menyimpan koleksi bacaan ke Pilihanku...')
      // likeType = 'create'
      likeMutation.mutate(
        { bookCollectionId: data.id },
        {
          onSuccess: () => {
            // toast.dismiss(toastId)
            // toast.custom(() => <SaveToast data={data} renderLink={renderLoveBookCollectionLink} type={likeType} />, {
            // duration: 10 * 1000,
            // })
          },
          onError: () => {
            // toast.dismiss(toastId)
            toast.error('Gagal menyimpan koleksi bacaan ke Pilihanku')
          },
        },
      )
    }
  }

  return (
    <Button
      variant='ghost'
      disabled={isPending}
      onClick={handleClick}
      className={cn(
        'srd:w-fit srd:gap-x-2 srd:p-2 srd:rounded-2xl srd:text-neutral-white',
        isLoved && 'srd:bg-blue-100/10',
      )}
    >
      <FaveIcon size={20} color='currentColor' />
      Simpan
    </Button>
  )
}

// interface SaveToastProps {
//   data: Pick<BookCollectionDetails, 'coverUrl' | 'name'>
//   renderLink: RenderLink
//   type: 'create' | 'delete'
// }

// function SaveToast({ data, renderLink, type }: SaveToastProps) {
//   if (type === 'delete') {
//     return (
//       <div className='srd:w-full srd:rounded-lg srd:shadow-lg srd:bg-complementary-purple-20 srd:p-4'>
//         <p className='srd:flex srd:items-start srd:text-sm'>
//           <Check className='srd:w-4 srd:h-4 srd:mr-2 srd:shrink-0' />
//           {data.name} telah dikeluarkan dari Pilihanku
//         </p>
//       </div>
//     )
//   }

//   return (
//     <div className='srd:flex srd:items-center srd:justify-between srd:w-full srd:rounded-lg srd:shadow-lg srd:bg-complementary-purple-20 srd:gap-x-4 srd:p-4'>
//       <div className='srd:flex srd:items-center srd:gap-x-2'>
//         <img
//           src={data.coverUrl}
//           alt={data.name}
//           width={60}
//           height={60}
//           draggable={false}
//           className='srd:rounded-md srd:aspect-5/4 srd:object-cover srd:overflow-clip srd:shrink-0'
//         />
//         <p className='srd:text-complementary-purple-100 srd:font-semibold srd:text-xs'>
//           {data.name} tersimpan di Pilihanku
//         </p>
//       </div>
//       <Button variant='outline' className='srd:w-fit srd:h-fit srd:px-2 srd:py-1 srd:text-xs' size='sm' asChild>
//         {renderLink({
//           children: 'Kunjungi Pilihanku',
//           className: 'srd:w-fit srd:h-fit srd:shrink-0',
//         })}
//       </Button>
//     </div>
//   )
// }
